<script setup>
import {watch, onMounted, computed, onBeforeUnmount, ref, nextTick} from "vue";
import {useFixtureForm} from "~/composables/useFixtureForm";
import {SESSION_MOVE_QUESTION} from "~/utils/sessionMove";
import ScoringSystemInput from "~/components/fields/ScoringSystemInput.vue";
import SimpleDateTimeSelect from "~/components/fields/SimpleDateTimeSelect.vue";
import PlayingAreaSelect from "~/components/fields/PlayingAreaSelect.vue";
import TimeAndPlacePicker from "~/components/bookings/TimeAndPlacePicker.vue";
import {onBeforeRouteLeave} from "vue-router";
import ClubMailerForm from "~/components/admin/ClubMailerForm.vue";
import {useFormData} from "~/composables/useFormData.js";
import {endpoints} from "~/network/endpoints";
import {errorAlert} from "~/utils/adminActionCopy";
import {afterDeleteRoute, clearTimeMessage, deleteMatchMessage, deleteMatchPenaltyNote, matchName} from "~/utils/matchActions";
import {useAuthService} from "@/composables/useAuthService";
import {useValidation} from "@/composables/useValidation";

const {addListener, removeLocalListenersWithEvent, notify} = useListenerService();
const route = useRoute();
const router = useRouter();

const editingFixtureId = computed(() => route.query.editingFixtureId);
const schedulingFixtureId = computed(() => route.query.schedulingFixtureId);
const deletingFixtureId = computed(() => route.query.deletingFixtureId);
const ladderChallengeId = computed(() => route.query.ladderChallengeId);
const schedulingLadderChallengeId = computed(() => route.query.schedulingLadderChallengeId);
const leagueCycleDivisionId = computed(() => route.query.leagueCycleDivisionId);
const schedulingLeagueCycleDivisionId = computed(() => route.query.schedulingLeagueCycleDivisionId);
const homeTeamId = computed(() => route.query.homeTeamId);
const awayTeamId = computed(() => route.query.awayTeamId);

// The query-param dialogs below only open once the page is mounted: opening a
// dialog during hydration (a deep link such as ?cancelingScheduleFixtureId=…)
// threw "Cannot read properties of null (reading 'classList')" (audit N18).
// X219 (R89 M-4): the edit / schedule dialog too (?schedulingFixtureId=…,
// ?schedulingLeagueCycleDivisionId&homeTeamId&awayTeamId…): loaded straight
// from a link it opened as the page hydrated and the page behind it rendered
// Nuxt's error page ("reading 'classList'").
const mounted = ref(false);

const editingOrDeleting = computed({
  get: () => {
    if (!mounted.value) return false;
    return (editingFixtureId.value !== undefined || schedulingFixtureId.value !== undefined ||
        (leagueCycleDivisionId.value !== undefined && homeTeamId.value !== undefined && awayTeamId.value !== undefined) ||
        (schedulingLeagueCycleDivisionId.value !== undefined && homeTeamId.value !== undefined && awayTeamId.value !== undefined) ||
        (ladderChallengeId.value !== undefined || schedulingLadderChallengeId.value !== undefined)) || deletingFixtureId.value !== undefined;
  },
  set: (value) => {
    if (value === false) {
      const divisionId = route.query.leagueCycleDivisionId || route.query.schedulingLeagueCycleDivisionId;

      if (router.options.history.state.back) {
        router.back();
      } else {
        let newQuery = {...route.query};
        delete newQuery.deletingFixtureId;
        delete newQuery.editingFixtureId;
        delete newQuery.schedulingFixtureId;
        delete newQuery.leagueCycleDivisionId;
        delete newQuery.schedulingLeagueCycleDivisionId;
        delete newQuery.homeTeamId;
        delete newQuery.awayTeamId;
        delete newQuery.ladderChallengeId;
        delete newQuery.schedulingLadderChallengeId;
        delete newQuery.scheduleMode;

        const newRoute = {...route, query: newQuery};
        if (divisionId) {
          newRoute.hash = `#division-${divisionId}`;
        }
        router.replace(newRoute);
      }

      // Scroll to division after route update
      if (divisionId) {
        setTimeout(() => {
          const element = document.getElementById(`division-${divisionId}`);
          if (element) {
            element.scrollIntoView({behavior: 'smooth', block: 'start'});
          }
        }, 100);
      }
    }
  },
});

const action = computed(() => {
  if (deletingFixtureId.value !== undefined) return 'delete';
  if (editingFixtureId.value !== undefined || schedulingFixtureId.value !== undefined) return 'update';
  if (leagueCycleDivisionId.value !== undefined && homeTeamId.value !== undefined && awayTeamId.value !== undefined && !loading.value) return 'create';
  if (schedulingLeagueCycleDivisionId.value !== undefined && homeTeamId.value !== undefined && awayTeamId.value !== undefined && !loading.value) return 'create';
  if ((route.query.schedulingLadderChallengeId !== undefined || route.query.ladderChallengeId !== undefined) && !loading.value) return 'create';
  return null;
})

const {t} = useI18n();

watch(() => editingOrDeleting.value, (newVal) => {
  if (newVal) {
    load();
  }
});

// Closed by dropping the query param, never router.back(): the entry behind
// this one is not always this page without the param (deep link, reload,
// arrived from another page's cell), and then "back" left the page.
const dropQuery = (key) => {
  const {[key]: _dropped, ...query} = route.query;
  return router.replace({...route, query});
};

// The match the open confirm is about, so the dialog can name it ("Sam v Alex")
// and say whether a ladder challenge goes with it (audit A4).
const {item: confirmFixture, getItem: getConfirmFixture} = useCrudApi("api/v2/fixtures");
const confirmFixtureLoading = ref(false);
const loadConfirmFixture = async (id) => {
  if (!id) return;
  if (confirmFixture.value?.id === id) return;
  confirmFixture.value = null;
  confirmFixtureLoading.value = true;
  try {
    await getConfirmFixture(id);
  } catch {
    // the confirm still works without the names
  } finally {
    confirmFixtureLoading.value = false;
  }
  await loadConfirmLadder(confirmFixture.value);
};

// The ladder's decline penalty, for the Delete match confirm. It lives on the
// ladder: the challenge nested in the match never carries it (the API leaves
// those two fields empty). Without it the confirm simply has no penalty line.
const {item: confirmLadder, getItem: getConfirmLadder} = useCrudApi("api/v2/ladders");
const loadConfirmLadder = async (fixture) => {
  const ladderId = fixture?.ladderChallengeId ? fixture.ladderId : null;
  if (!ladderId || confirmLadder.value?.id === ladderId) return;
  confirmLadder.value = null;
  try {
    await getConfirmLadder(ladderId);
  } catch {
    // no penalty line, never a wrong one
  }
};

// Delete match dialog (league and ladder matches — the API deletes the match,
// and for a ladder the challenge too). The param keeps its old name.
const cancelingScheduleFixtureId = computed(() => route.query.cancelingScheduleFixtureId);
const cancelScheduleDialog = computed({
  get: () => mounted.value && cancelingScheduleFixtureId.value !== undefined,
  set: (value) => {
    if (value === false) {
      dropQuery('cancelingScheduleFixtureId');
    }
  }
});

// Deleting a challenge's unplayed match cancels the challenge: for the
// challenged side (not as a club admin) that counts as declining, so the
// confirm adds the ladder's penalty, as the challenge page does (owner, 3 Nov 2026).
const {user, isAuthenticated, isAdminOfClub} = useAuthService();
const {myTeamFromListOfTeams} = useValidation();
const deleteConfirmMessage = computed(() => {
  const f = confirmFixture.value;
  const myTeam = f && isAuthenticated.value && user.value
      ? myTeamFromListOfTeams(user.value.playerId, [f.homeTeam, f.awayTeam].filter(Boolean))
      : null;
  const clubId = f?.clubId ?? f?.ladderChallenge?.clubId;
  const clubAdmin = !!(clubId && isAuthenticated.value && isAdminOfClub(clubId));
  return [deleteMatchMessage(f), deleteMatchPenaltyNote(f, myTeam?.id ?? null, clubAdmin,
      confirmLadder.value?.id === f?.ladderId ? confirmLadder.value : null)].filter(Boolean).join(' ');
});

const {item : deleteFixtureItem, deleteItem: deleteFixture, formPending: deleteFixturePending, formErrors: deleteFixtureErrors, hasFormErrors: hasDeleteFixtureErrors} = useCrudApi("api/v2/fixtures");
const {item : clearFixtureScheduleItem, deleteItem: clearFixtureSchedule, formPending: clearSchedulePending, formErrors: clearScheduleErrors, hasFormErrors: hasClearScheduleErrors} = useCrudApi("api/v2/fixtures");
const {item: clearFixtureResultItem, deleteItem: clearFixtureResult, formPending: clearResultPending, formErrors: clearResultErrors, hasFormErrors: hasClearResultErrors} = useCrudApi("api/v2/fixtures");

const cancelScheduleAction = async () => {
  const id = cancelingScheduleFixtureId.value;
  const deleted = confirmFixture.value?.id === id ? confirmFixture.value : null;
  try {
    await deleteFixture(id);
  } catch {
    // errors land in deleteFixtureErrors
  }
  if (hasDeleteFixtureErrors.value) {
    setAlertMessage(errorAlert(deleteFixtureErrors.value, 'The match could not be deleted'));
    return;
  }
  setAlertMessage(`${deleted ? matchName(deleted) : 'Match'} deleted`);
  // On the deleted match's own page there is nothing left to show.
  if (route.params.fixtureId && route.params.fixtureId === id) {
    await router.replace(afterDeleteRoute(deleted));
    return;
  }
  await dropQuery('cancelingScheduleFixtureId');
  notify("fixtureUpdated", deleteFixtureItem.value);
};

// Clear result dialog (tournament fixtures)
const clearingResultFixtureId = computed(() => route.query.clearingResultFixtureId);
// Closed by dropping the query param, never router.back(): the entry behind
// this one is not always this page without the param (deep link, reload,
// arrived from another page's cell), and then "back" left the dialog open.
const closeClearResultDialog = () => {
  const {clearingResultFixtureId: _cleared, ...query} = route.query;
  return router.replace({...route, query});
};
const clearResultDialog = computed({
  get: () => mounted.value && clearingResultFixtureId.value !== undefined,
  set: (value) => {
    if (value === false) {
      closeClearResultDialog();
    }
  }
});

const {setAlertMessage} = useAlertService();
const clearResultAction = async () => {
  try {
    await clearFixtureResult(clearingResultFixtureId.value + '/result');
  } catch {
    // errors land in clearResultErrors
  }
  if (hasClearResultErrors.value) {
    setAlertMessage(errorAlert(clearResultErrors.value, 'The result could not be cleared'));
  } else {
    await closeClearResultDialog();
    setAlertMessage({message: 'Result cleared', color: 'success'});
    notify("fixtureUpdated", clearFixtureResultItem.value);
  }
};

// Clear time dialog (generated matches: tournament draws and americano
// rotations — the match is kept, only its time and court go).
const clearingScheduleFixtureId = computed(() => route.query.clearingScheduleFixtureId);
const clearScheduleDialog = computed({
  get: () => mounted.value && clearingScheduleFixtureId.value !== undefined,
  set: (value) => {
    if (value === false) {
      dropQuery('clearingScheduleFixtureId');
    }
  }
});

const clearScheduleAction = async () => {
  try {
    await clearFixtureSchedule(clearingScheduleFixtureId.value + '/schedule');
  } catch {
    // errors land in clearScheduleErrors
  }
  if (hasClearScheduleErrors.value) {
    setAlertMessage(errorAlert(clearScheduleErrors.value, 'The time could not be cleared'));
    return;
  }
  await dropQuery('clearingScheduleFixtureId');
  setAlertMessage('Time cleared');
  notify("fixtureUpdated", clearFixtureScheduleItem.value);
};

watch([cancelingScheduleFixtureId, clearingScheduleFixtureId, mounted], ([deleteId, clearId, isMounted]) => {
  if (isMounted) loadConfirmFixture(deleteId || clearId);
});

const {
  scoringSystem, gameData, homeResult, awayResult, homeGames, awayGames, fixture,
  homeTeam, awayTeam, formType, isAdmin, canEdit, gamePossible, canCreate, isApproved, loading,
  title, save, fixtureErrors, fixtureSaveLoading, statusNote, buttonText, loadFixture,
  scorers, isScheduleForm, scheduleNote, scheduleBlock, scheduleFormErrors,
  linker, requiresGameScores, tiebreakGameNumber, handicapStart,
  scheduling, pickerChoice, pickerMode, isScheduleAdmin, otherSideName, reloadScheduling,
  sessionScope, askSessionMove, sessionMoveChoices, pickerKept, refusesPastTimes, outsideHoursAsk, bookOutsideHours, pastTimeBlocked
} = useFixtureForm({});
// X244 (TEST_R102 M-A): the Book anyway question for a whole session moved outside the hours to a time off the table.
const outsideHoursOpen = computed({get: () => !!outsideHoursAsk.value, set: (v) => { if (!v) outsideHoursAsk.value = null; }});
// B7b: the time-and-place picker replaces the date, time and playing-area inputs
// only for clubs that take bookings (null = not known yet, so neither shows).
const schedulingActive = computed(() => scheduling.active.value);
const schedulingData = computed(() => scheduling.availability.value);
const schedulingLoading = computed(() => scheduling.loading.value);
// X214: wide for the court table, a plain width for the date and time alone, the usual for results.
const scheduleDialogWidth = computed(() => {
  if (!isScheduleForm.value) return '1200';
  return schedulingActive.value === false ? '640' : '1120';
});

// F-FIX6 (TEST_R99 L-1): a refused save's reason sits under the summary, below the visible part of a tall schedule
// dialog (under its sticky footer); bring it into view when it appears.
const scheduleErrorBox = ref(null);
watch(() => scheduleFormErrors.value, (errs) => {
  const any = errs && typeof errs === 'object' && Object.values(errs).some((v) => Array.isArray(v) ? v.length : !!v);
  if (!any) return;
  nextTick(() => {
    try { scheduleErrorBox.value?.scrollIntoView?.({block: 'center', behavior: 'smooth'}); } catch { /* no layout (tests) */ }
  });
}, {deep: true});

// The dialog shows `global` messages only; the API's model-binding errors come keyed by field
// ("games[2].homeTeamScore": ["Invalid value"]) and were invisible. Fold anything not already
// global into readable lines so a rejected save always says why.
const fixtureDisplayErrors = computed(() => {
  const errors = fixtureErrors.value;
  if (!errors || typeof errors !== 'object') return errors;
  const global = [...(errors.global ?? [])];
  for (const [key, messages] of Object.entries(errors)) {
    if (key === 'global' || key === 'request' || !Array.isArray(messages)) continue;
    const game = key.match(/^games\[(\d+)\]\.(home|away)TeamScore$/i);
    const label = game ? `Game ${Number(game[1]) + 1} ${game[2].toLowerCase()} score` : key;
    for (const m of messages) global.push(`${label}: ${m}`);
  }
  return global.length ? {...errors, global} : errors;
});


const load = () => {
  loadFixture({
    isDelete: route.query.deletingFixtureId !== undefined,
    isScheduleUpdate: route.query.schedulingFixtureId !== undefined,
    isScheduleCreate: route.query.schedulingLadderChallengeId !== undefined || route.query.schedulingLeagueCycleDivisionId !== undefined,
    fixtureId: route.query.editingFixtureId || route.query.deletingFixtureId || route.query.schedulingFixtureId,
    leagueCycleDivisionId: route.query.leagueCycleDivisionId || route.query.schedulingLeagueCycleDivisionId,
    ladderChallengeId: route.query.ladderChallengeId,
    schedulingLadderChallengeId: route.query.schedulingLadderChallengeId,
    // which button opened the schedule dialog (ladder challenge page), for its title
    scheduleMode: route.query.scheduleMode,
    homeTeamId: route.query.homeTeamId,
    awayTeamId: route.query.awayTeamId,
  });
}

onMounted(() => {
  // opens the query-param dialogs; the editingOrDeleting watch loads the match (X219)
  mounted.value = true;
  addListener("fixtureUpdated", () => {
    // Only close the edit dialog when it is open: closing a closed one ran
    // router.back() and left the page after a delete/clear elsewhere.
    if (editingOrDeleting.value) {
      editingOrDeleting.value = false;
    }
  });
});
onBeforeUnmount(() => {
  removeLocalListenersWithEvent("fixtureUpdated");
});

</script>

<template>
  <div>
    <slot/>
    <club-mailer-form/>

    <!-- Delete match (league and ladder matches; the param keeps its old name) -->
    <tl-dialog v-model="cancelScheduleDialog" title="Delete match?" :max-width="420">
      <p class="mb-0">
        <template v-if="confirmFixtureLoading">Loading the match…</template>
        <template v-else>{{ deleteConfirmMessage }}</template>
      </p>
      <crud-error-display :errors="deleteFixtureErrors" show-all class="mt-3"/>
      <template #actions>
        <v-btn variant="outlined" :disabled="deleteFixturePending" @click="cancelScheduleDialog = false">Cancel</v-btn>
        <v-btn color="error" variant="flat" prepend-icon="mdi-delete"
               :loading="deleteFixturePending" :disabled="confirmFixtureLoading"
               @click="cancelScheduleAction">
          Delete match
        </v-btn>
      </template>
    </tl-dialog>

    <!-- Clear result (generated matches) -->
    <tl-dialog v-model="clearResultDialog" title="Clear result?" :max-width="420">
      <p class="mb-0">
        The score is removed from this match, and the match itself stays in place ready for a new result. Both players are emailed.
      </p>
      <crud-error-display :errors="clearResultErrors" show-all class="mt-3"/>
      <template #actions>
        <v-btn variant="outlined" :disabled="clearResultPending" @click="clearResultDialog = false">Cancel</v-btn>
        <v-btn color="error" variant="flat" prepend-icon="mdi-eraser"
               :loading="clearResultPending" @click="clearResultAction">
          Clear result
        </v-btn>
      </template>
    </tl-dialog>

    <!-- Clear time (generated matches: tournament draws, americano rotations) -->
    <tl-dialog v-model="clearScheduleDialog" title="Clear time?" :max-width="420">
      <p class="mb-0">
        <template v-if="confirmFixtureLoading">Loading the match…</template>
        <template v-else>{{ clearTimeMessage(confirmFixture) }}</template>
      </p>
      <crud-error-display :errors="clearScheduleErrors" show-all class="mt-3"/>
      <template #actions>
        <v-btn variant="outlined" :disabled="clearSchedulePending" @click="clearScheduleDialog = false">Cancel</v-btn>
        <v-btn color="error" variant="flat" prepend-icon="mdi-calendar-remove"
               :loading="clearSchedulePending" @click="clearScheduleAction">
          Clear time
        </v-btn>
      </template>
    </tl-dialog>

    <!-- X244: a whole session moved outside the hours to a time the table doesn't offer (the X237 question) -->
    <tl-dialog v-model="outsideHoursOpen" :title="outsideHoursAsk?.title" :max-width="400">
      <p class="mb-0">{{ outsideHoursAsk?.body }}</p>
      <template #actions>
        <v-btn variant="text" @click="outsideHoursOpen = false">{{ outsideHoursAsk?.no }}</v-btn>
        <v-btn color="primary" variant="flat" @click="bookOutsideHours">{{ outsideHoursAsk?.yes }}</v-btn>
      </template>
    </tl-dialog>

    <lazy-crud-form-dialog
        :create-title="title"
        :update-title="title"
        :form-pending="fixtureSaveLoading"
        :btn-text="action === 'delete' ? t('delete_fixture') : buttonText"
        :delete-title="t('delete_fixture')"
        :action="action"
        :item-pending="loading"
        v-model="fixture"
        :disabled="!(canCreate || canEdit) || !!scheduleBlock || pastTimeBlocked"
        :save-action="save"
        :max-dialog-width="scheduleDialogWidth"
        :loader-min-height="isScheduleForm ? 420 : null"
        v-model:dialog="editingOrDeleting">
      <div v-if="isScheduleForm">
        <!-- B7b: clubs that take bookings pick the time and place together; the rest keep these inputs.
             X214: When, then Book a court (the /book table), then the summary; the button says what happens. -->
        <v-skeleton-loader v-if="schedulingActive === null" type="heading, list-item-two-line, image" class="schedule-skeleton"/>
        <time-and-place-picker
            v-else-if="schedulingActive && schedulingData"
            v-model="pickerChoice"
            :availability="schedulingData"
            :mode="pickerMode"
            :other-name="otherSideName"
            :preferred-area-id="fixture.playingAreaId"
            :admin="isScheduleAdmin"
            :loading="schedulingLoading"
            :note="scheduleNote"
            :kept="pickerKept"
            :refuses-past="refusesPastTimes"
            @reload="reloadScheduling"
        />
        <template v-else>
          <simple-date-time-select
              v-model:date="fixture.date"
              v-model:time="fixture.time"
              :date-error="fixtureErrors.date != null"
              :date-error-messages="fixtureErrors.date"
              :time-error="fixtureErrors.time != null"
              :time-error-messages="fixtureErrors.time"
          />
          <playing-area-select
              v-model="fixture.playingAreaId"
              :club-id="fixture.clubId || linker?.clubId"
              :sport-id="fixture.sportId"
              :exclude-fixture-id="fixture.id"
              :date="fixture.date"
              :time="fixture.time"
          />
        </template>
        <!-- what saving does: a proposal, a confirm, or an admin's set time (the picker says it in its summary) -->
        <div v-if="scheduleNote && !(schedulingActive && schedulingData)" class="d-flex align-center ga-2 mt-2 text-body-2 text-medium-emphasis">
          <v-icon size="16" icon="mdi-information-outline"/>
          <span>{{ scheduleNote }}</span>
        </div>
        <!-- X239: an americano match that shares its session: move just it, or every round on every court -->
        <div v-if="askSessionMove" class="mt-4" data-test="session-move">
          <div class="text-body-2 font-weight-medium mb-1">{{ SESSION_MOVE_QUESTION }}</div>
          <v-radio-group v-model="sessionScope" hide-details>
            <v-radio v-for="o in sessionMoveChoices" :key="o.value" :value="o.value" :disabled="!!o.disabled">
              <template #label>
                <!-- X242 (TEST_R101 L-b): a disabled choice reads disabled, title and subtitle in the kit's ink-3 (textSubtle) -->
                <div class="ml-2">
                  <div :class="{'text-textSubtle': o.disabled}">{{ o.title }}</div>
                  <small class="text-textSubtle">{{ o.subtitle }}</small>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </div>
        <v-alert v-if="scheduleBlock" type="warning" variant="tonal" density="compact" class="mt-3">{{ scheduleBlock }}</v-alert>
        <!-- X247 (TEST_R104 L-3): Set is off for a ladder time that has passed; without the picker this says why -->
        <v-alert v-if="pastTimeBlocked && !scheduleBlock && !(schedulingActive && schedulingData)" type="warning" variant="tonal" density="compact" class="mt-3">That time has already passed.</v-alert>
        <!-- the API's reason when it refuses (e.g. a challenge not accepted yet) -->
        <div ref="scheduleErrorBox"><crud-error-display :errors="scheduleFormErrors" show-all class="mt-3"/></div>
      </div>
      <v-container max-width="800" v-else>
        <!-- Context note: makes clear whether the user is approving, editing or recording. -->
        <div v-if="statusNote" class="d-flex align-center ga-2 mb-4 text-body-2 text-medium-emphasis">
          <v-icon size="16" icon="mdi-information-outline"/>
          <span>{{ statusNote }}</span>
        </div>

        <!-- every result-entry path also offers live scoring for unplayed, live-capable fixtures -->
        <v-btn
            v-if="fixture?.id && fixture.supportsLiveScoring && fixture.homeTeamResult == null"
            variant="outlined" size="small" class="text-none mb-4"
            prepend-icon="mdi-scoreboard-outline"
            @click="$router.push(`/fixtures/${fixture.id}/live`)"
        >Score it live instead</v-btn>

        <scoring-system-input
            :home-team="homeTeam"
            :away-team="awayTeam"
            :require-game-scores="requiresGameScores"
            :tiebreak-game-number="tiebreakGameNumber"
            :overrides="fixture?.scoringOverrides ?? []"
            :handicap-start="handicapStart"
            :read-only="!(canCreate || canEdit)"
            v-model:scoring-system="scoringSystem"
            v-model:away-result="awayResult"
            v-model:home-result="homeResult"
            v-model:games="gameData"
            v-model:scorers="scorers"/>

        <v-alert
            v-if="canEdit && fixture && fixture.ladderChallengeId != null && formType === 'edit' && isApproved"
            color="error">
          Editing this Ladder match will result in position increases/decreases for the players involved from the
          previous entry being removed and recalculated. You can manually adjust the positions after saving if
          required
          through the admin dashboard.
        </v-alert>
        <crud-error-display :errors="fixtureDisplayErrors"></crud-error-display>
      </v-container>

    </lazy-crud-form-dialog>
  </div>
</template>

<style scoped lang="scss">
.schedule-skeleton {
  min-height: 420px;
  background: transparent;
}
</style>
